/**
 * @file CommonTooltip.js
 * @brief Common Tooltip component wrapper for consistent tooltip styling across the application
 * @author FYTN Development Team
 * @date 2025
 * 
 * This component provides a standardized tooltip implementation using Material-UI's Tooltip
 * with consistent styling and behavior across the application.
 */

import React from 'react';
import { Tooltip as MuiTooltip } from '@mui/material';

/**
 * CommonTooltip - A wrapper around Material-UI Tooltip with consistent styling
 * 
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {string} title - The text to display in the tooltip
 * @param {string} placement - Tooltip placement (top, bottom, left, right). Default: 'top'
 * @param {object} sx - Additional Material-UI sx styles
 * @param {boolean} arrow - Whether to show an arrow. Default: true
 * @param {number} enterDelay - Delay before showing tooltip (ms). Default: 300
 * @param {number} leaveDelay - Delay before hiding tooltip (ms). Default: 0
 * @param {object} ...props - Other Material-UI Tooltip props
 * 
 * @returns {React.Component} Tooltip component
 * 
 * @example
 * <CommonTooltip title="Click to edit" placement="right">
 *   <IconButton>
 *     <EditIcon />
 *   </IconButton>
 * </CommonTooltip>
 */
const CommonTooltip = ({ 
  children, 
  title, 
  placement = 'top',
  arrow = true,
  enterDelay = 300,
  leaveDelay = 0,
  sx = {},
  ...props 
}) => {
  // Don't render tooltip if no title is provided - return children directly without wrapper
  if (!title || title.trim() === "") {
    return <>{children}</>;
  }

  return (
    <MuiTooltip
      title={title}
      placement={placement}
      arrow={arrow}
      enterDelay={enterDelay}
      leaveDelay={leaveDelay}
      TransitionProps={{
        timeout: { enter: 200, exit: 150 },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            background: 'rgba(128, 128, 128, 0.95) !important', // Grey background
            backdropFilter: 'blur(10px) saturate(180%)',
            WebkitBackdropFilter: 'blur(10px) saturate(180%)',
            fontSize: '0.9375rem !important',
            fontWeight: '500 !important',
            padding: '12px 18px !important',
            borderRadius: '12px !important',
            maxWidth: '300px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), 0 2px 8px rgba(0, 0, 0, 0.2) !important',
            border: '1px solid rgba(255, 255, 255, 0.2) !important',
            color: '#ffffff !important', // White text for contrast with grey background
            letterSpacing: '0.3px',
            lineHeight: '1.5 !important',
            ...sx,
          },
        },
        arrow: {
          sx: {
            color: 'rgba(128, 128, 128, 0.95) !important', // Grey background
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))',
          },
        },
      }}
      {...props}
    >
      {children}
    </MuiTooltip>
  );
};

export default CommonTooltip;

